import { cn } from 'cn';
import { type PropsWithChildren } from 'react';

import { InputLabel } from './InputLabel';
import { ValidationError } from './ValidationError';

export type FieldErrorDisplay = 'block' | 'none' | 'overlay' | 'push';

export type FieldShellProps = PropsWithChildren<{
  readonly errorDisplay?: FieldErrorDisplay;
  readonly errors?: readonly string[];
  readonly hint?: string;
  readonly inputId?: string;
  readonly isRequired?: boolean;
  readonly label?: string;
}> &
  ComponentStyling;

const EMPTY_ERRORS: readonly string[] = [];

export function getFieldDescriptionIds({
  hasErrors,
  hasHint,
  inputId,
}: {
  hasErrors: boolean;
  hasHint: boolean;
  inputId: string;
}): string | undefined {
  const ids = [
    hasHint ? `${inputId}-hint` : undefined,
    hasErrors ? `${inputId}-error` : undefined,
  ].filter((id): id is string => Boolean(id));

  return ids.length > 0 ? ids.join(' ') : undefined;
}

export function FieldShell({
  children,
  className,
  errorDisplay = 'overlay',
  errors = EMPTY_ERRORS,
  hint,
  inputId,
  isRequired,
  label,
  style,
}: FieldShellProps) {
  const errorMessage = errors.join(', ');
  const hasErrors = Boolean(errorMessage);
  const errorId = inputId ? `${inputId}-error` : undefined;
  const hintId = inputId && hint ? `${inputId}-hint` : undefined;

  return (
    <div
      className={cn(errorDisplay === 'overlay' && 'relative', className)}
      style={style}
    >
      {label ? (
        <InputLabel hint={hint} htmlFor={inputId} isRequired={isRequired}>
          {label}
        </InputLabel>
      ) : null}
      {hintId ? (
        <span className="sr-only" id={hintId}>
          {hint}
        </span>
      ) : null}
      {children}
      {errorDisplay === 'block' ? (
        <div className="min-h-5" id={errorId}>
          {hasErrors ? (
            <ValidationError className="static block translate-y-0 bg-transparent leading-tight">
              {errorMessage}
            </ValidationError>
          ) : null}
        </div>
      ) : null}
      {errorDisplay === 'push' && hasErrors ? (
        <div id={errorId}>
          <ValidationError className="static block translate-y-0 bg-transparent leading-tight">
            {errorMessage}
          </ValidationError>
        </div>
      ) : null}
      {errorDisplay === 'overlay' && hasErrors ? (
        <span id={errorId}>
          <ValidationError>{errorMessage}</ValidationError>
        </span>
      ) : null}
    </div>
  );
}
